import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Button } from 'components/index';
import { ReportLineItem, ReportLineItemNotations, ReportLineItemTypes } from './report.type';
import SelectInput from '../SelectInput';
import { Division, LineItemLabelEnum, LineItemUnitEnum } from '../glossary/glossary.type';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Close, QuestionMark } from '@mui/icons-material';
import NumericFormatInput from '../general/NumericFormatInput';

interface LineItemEditDialogProps {
  open: boolean;
  onClose: () => void;
  lineItem: ReportLineItem | null;
  updateLineItem: (lineItem: ReportLineItem) => void;
  divisions: Division[];
}

const areSame = (a: Partial<ReportLineItem>, b: Partial<ReportLineItem>, initialNoteContent: string, currentNoteContent: string) => {
  return (
    a.totalBudget === b.totalBudget &&
    a.quantity === b.quantity &&
    a.unit === b.unit &&
    a.division?.id === b.division?.id &&
    a.cdccCompPerUnit === b.cdccCompPerUnit &&
    a.label === b.label &&
    a.notation === b.notation &&
    initialNoteContent === currentNoteContent
  );
};

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];
const notationsList = [...Object.values(ReportLineItemNotations).map(notation => ({ value: notation, label: notation }))];
const labelsList = [...Object.values(LineItemLabelEnum).map(label => ({ value: label, label: label })), { label: 'None', value: '' }];

const LineItemEditDialog: React.FC<LineItemEditDialogProps> = ({ open, onClose, lineItem: initialLineItem, updateLineItem, divisions }) => {
  const initialOrDefaultValues = {
    totalBudget: initialLineItem?.totalBudget ?? 0,
    quantity: initialLineItem?.quantity ?? 0,
    unit: initialLineItem?.unit ?? null,
    cdccCompPerUnit: initialLineItem?.cdccCompPerUnit ?? 0,
    division: initialLineItem?.division ?? null,
    updateDivisionGlobally: initialLineItem?.updateDivisionGlobally ?? true,
    label: initialLineItem?.label ?? null,
    noteContent: initialLineItem?.note?.content || '',
    notation: initialLineItem?.notation,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      totalBudget: yup.number().required('Required').typeError('Must be a number'),
      quantity: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
      cdccCompPerUnit: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
    }),
    onSubmit: values => {
      if (!initialLineItem) return;

      updateLineItem({
        ...initialLineItem,
        ...values,
        note: { ...initialLineItem.note, content: values.noteContent },
      });
      formik.resetForm();
      onClose();
    },
  });

  const updateDivision = (newVal: string | null) => {
    if (!initialLineItem) return;
    const newDivision = newVal ? divisions.find(division => division.id === newVal) : null;
    formik.setFieldValue('division', newDivision ?? null);
  };

  const isTotal = Boolean(initialLineItem && initialLineItem.type != null && initialLineItem.type != ReportLineItemTypes.NORMAL);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {initialLineItem?.lineItem.description ?? 'Edit Line Item'}
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {initialLineItem ? (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} py={2}>
              <TextField
                name='totalBudget'
                label='Total Budget'
                value={formik.values.totalBudget}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.totalBudget && Boolean(formik.errors.totalBudget)}
                helperText={formik.touched.totalBudget && formik.errors.totalBudget}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
                disabled={isTotal}
              />
              <TextField
                name='quantity'
                label='Quantity'
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                helperText={formik.touched.quantity && formik.errors.quantity}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
                disabled={isTotal}
              />
              <SelectInput
                label='Unit'
                selected={formik.values.unit ?? ''}
                setSelected={newVal => {
                  formik.setFieldValue('unit', newVal === '' ? null : newVal);
                }}
                list={unitsList}
                disabled={isTotal}
              />
              <TextField
                name='cdccCompPerUnit'
                label='CDCC Comp / Unit'
                value={formik.values.cdccCompPerUnit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.cdccCompPerUnit && Boolean(formik.errors.cdccCompPerUnit)}
                helperText={formik.touched.cdccCompPerUnit && formik.errors.cdccCompPerUnit}
                variant='standard'
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
                inputProps={{
                  prefix: '$',
                }}
                disabled={isTotal}
              />
              <Stack direction='row' justifyContent='space-between' alignItems={'center'} spacing={2}>
                <SelectDivisionFromGiven
                  label='Division'
                  divisions={divisions}
                  selectedDivision={formik.values.division?.id ?? null}
                  setSelectedDivision={updateDivision}
                  disabled={isTotal}
                  readOnly={initialLineItem.lineItem.description.startsWith('Comparable Pricing') ? true : false}
                />
                {initialLineItem && initialLineItem.lineItem.description.startsWith('Comparable Pricing') ? null : (
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          name='updateDivisionGlobally'
                          checked={formik.values.updateDivisionGlobally}
                          onChange={e => formik.setFieldValue('updateDivisionGlobally', e.target.checked)}
                          disabled={isTotal}
                        />
                      }
                      label={'Update Globally'}
                      labelPlacement='end'
                    />
                    <Tooltip title='When this toggle is ON, this line item will be moved to the new division in every report and project. When OFF, the change is for current report only!'>
                      <QuestionMark fontSize='small' />
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
              <Autocomplete
                freeSolo
                id='notation'
                options={notationsList}
                inputValue={formik.values.notation ?? ''}
                onInputChange={(_, newVal) => {
                  formik.setFieldValue('notation', newVal === '' ? null : newVal);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    name='notation'
                    label='Notation'
                    variant='standard'
                    sx={{ '& .MuiFormLabel-root.MuiInputLabel-root': { color: 'black' } }}
                  />
                )}
              />

              <SelectInput
                label='Label'
                selected={formik.values.label ?? ''}
                setSelected={newVal => {
                  formik.setFieldValue('label', newVal === '' ? null : newVal);
                }}
                list={labelsList}
                disabled={isTotal}
              />
              <TextField
                name='noteContent'
                label='Note'
                multiline
                rows={4}
                fullWidth
                value={formik.values.noteContent}
                onChange={e => {
                  const value = e.target.value;
                  if (value.trim() || value === '') {
                    formik.handleChange(e);
                  }
                }}
                placeholder='Add a note...'
                disabled={isTotal}
                style={{ marginTop: '16px' }}
              />
              <Alert severity='warning'>
                You need to click <b>Save</b> in the footer to avoid losing your changes!
              </Alert>
            </Stack>
          </form>
        ) : (
          <Alert severity='error'>No LineItem Selected</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={formik.submitForm}
          disabled={areSame(formik.values, formik.initialValues, initialLineItem?.note?.content || '', formik.values.noteContent)}
          // disabled={!formik.isValid || !formik.dirty}
        >
          Save Draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(LineItemEditDialog);
